import { useState } from 'react';
import { Star, BadgeCheck } from 'lucide-react';

const GOOGLE_REVIEW_URL = 'https://g.page/r/CQE9j7Ao2r5zEAE/review';
const GOOGLE_MAPS_URL = 'https://www.google.com/maps/place/TravelGuide.CR/@10.0780213,-84.2452673,17z/data=!3m1!4b1!4m6!3m5!1s0x8fa0f732cd7b6a49:0x73beda28b08f3d01!8m2!3d10.0780213!4d-84.2452673!16s%2Fg%2F11h4_q2vqj';
const TRIPADVISOR_URL = 'https://www.tripadvisor.com/Attraction_Review-g8627695-d13437753-Reviews-Travel_Guide_Costa_Rica-San_Pedro_de_Poas_Province_of_Alajuela.html';

const GoogleLogo = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

const TripAdvisorLogo = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="#00AF87" aria-hidden="true">
    <path d="M12 7.5c-2.6 0-5 .58-6.9 1.6H1l1.6 1.74A4.6 4.6 0 0 0 9.7 17.4 4.58 4.58 0 0 0 12 16.1a4.58 4.58 0 0 0 2.3 1.3 4.6 4.6 0 0 0 7.1-4.96L23 10.7h-4.1A14.4 14.4 0 0 0 12 7.5zm-4.6 9.05a2.95 2.95 0 1 1 0-5.9 2.95 2.95 0 0 1 0 5.9zm9.2 0a2.95 2.95 0 1 1 0-5.9 2.95 2.95 0 0 1 0 5.9zM7.4 12.1a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm9.2 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z" />
  </svg>
);

const AVATAR_COLORS = ['#1D4E41', '#F08A5D', '#2d6a4f', '#c2410c', '#0f766e', '#9a3412'];
const avatarColor = (name) => AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

const Stars = ({ size = 'w-4 h-4' }) => (
  <span className="inline-flex items-center gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`${size} fill-[#FFB400] text-[#FFB400]`} />
    ))}
  </span>
);

const ReviewCard = ({ review, lang }) => {
  const [expanded, setExpanded] = useState(false);
  const long = (review.content || '').length > 160;
  const text = expanded || !long ? review.content : `${review.content.slice(0, 160).trim()}…`;
  const Logo = review.source === 'google' ? GoogleLogo : TripAdvisorLogo;

  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 flex flex-col text-center items-center"
      data-testid={`review-card-${review.id}`}
    >
      <Logo className="w-7 h-7 mb-3" />
      <Stars />
      <h4 className="font-bold text-[#1A1A1A] mt-3 mb-2 leading-snug">{review.title}</h4>
      <p className="text-gray-600 text-sm leading-relaxed">{text}</p>
      {long && (
        <button
          onClick={() => setExpanded((v) => !v)}
          data-testid={`review-readmore-${review.id}`}
          className="text-sm text-gray-500 hover:text-[#1D4E41] font-medium mt-1"
        >
          {expanded ? (lang === 'es' ? 'Leer menos' : 'Read less') : (lang === 'es' ? 'Leer más' : 'Read more')}
        </button>
      )}
      {review.photos && review.photos.length > 0 && (
        <div className={`grid gap-2 mt-4 w-full ${review.photos.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`} data-testid={`review-photos-${review.id}`}>
          {review.photos.slice(0, 4).map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`${review.author} review photo ${i + 1}`}
              loading="lazy"
              decoding="async"
              className="w-full h-28 object-cover rounded-lg border border-gray-100"
            />
          ))}
        </div>
      )}
      <div className="mt-auto pt-5 flex flex-col items-center">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg mb-2"
          style={{ backgroundColor: avatarColor(review.author) }}
        >
          {(review.author || '?').trim().charAt(0).toUpperCase()}
        </div>
        <div className="flex items-center gap-1">
          <span className="font-semibold text-[#1A1A1A] text-sm">{review.author}</span>
          <BadgeCheck className="w-4 h-4 text-[#1D4E41]" />
        </div>
        <span className="text-xs text-gray-400 mt-0.5">{review.date}</span>
      </div>
    </div>
  );
};

const ReviewsWidget = ({ reviews = [], lang = 'en' }) => {
  const [tab, setTab] = useState('all');

  const googleReviews = reviews.filter((r) => r.source === 'google');
  const tripReviews = reviews.filter((r) => r.source === 'tripadvisor');
  const filtered = tab === 'google' ? googleReviews : tab === 'tripadvisor' ? tripReviews : reviews;

  const tabs = [
    { id: 'all', label: lang === 'es' ? 'Todas' : 'All Reviews', logo: null, count: reviews.length },
    { id: 'google', label: 'Google', logo: GoogleLogo, count: googleReviews.length },
    { id: 'tripadvisor', label: 'Tripadvisor', logo: TripAdvisorLogo, count: tripReviews.length },
  ].filter((tb) => tb.id === 'all' || tb.count > 0);

  return (
    <div data-testid="reviews-widget">
      {/* Platform tabs */}
      <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-b border-gray-200 pb-1 mb-8">
        {tabs.map((tb) => {
          const Logo = tb.logo;
          const active = tab === tb.id;
          return (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              data-testid={`reviews-tab-${tb.id}`}
              className={`flex items-center gap-2 pb-2 -mb-[5px] border-b-2 transition-colors ${
                active ? 'border-[#1D4E41] text-[#1A1A1A]' : 'border-transparent text-gray-500 hover:text-[#1A1A1A]'
              }`}
            >
              {Logo && <Logo className="w-5 h-5" />}
              <span className="font-semibold text-sm md:text-base">{tb.label}</span>
              <span className="font-bold text-sm md:text-base text-[#1A1A1A]">5.0</span>
            </button>
          );
        })}
      </div>

      {/* Summary header + review button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          {tab === 'google' ? <GoogleLogo className="w-8 h-8" /> : tab === 'tripadvisor' ? <TripAdvisorLogo className="w-8 h-8" /> : null}
          <div className="text-left">
            <p className="text-lg font-bold text-[#1A1A1A]">
              {tab === 'google' ? 'Google' : tab === 'tripadvisor' ? 'Tripadvisor' : (lang === 'es' ? 'Todas las reseñas' : 'All Reviews')}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-[#1A1A1A]">5.0</span>
              <Stars size="w-5 h-5" />
              <span className="text-sm text-gray-400">({filtered.length})</span>
            </div>
            {tab === 'google' && (
              <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" data-testid="see-google-reviews" className="text-xs font-medium text-[#1D4E41] hover:underline">
                {lang === 'es' ? 'Ver reseñas en Google →' : 'See reviews on Google →'}
              </a>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {tab !== 'tripadvisor' && (
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="review-google-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-gray-200 text-[#1A1A1A] font-semibold text-sm shadow-sm hover:border-[#1D4E41] hover:shadow-md transition-all"
            >
              <GoogleLogo className="w-4 h-4" />
              {lang === 'es' ? 'Reseñar en Google' : 'Review us on Google'}
            </a>
          )}
          {tab !== 'google' && (
            <a
              href={TRIPADVISOR_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="review-tripadvisor-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1D4E41] text-white font-semibold text-sm shadow-sm hover:bg-[#153A30] hover:shadow-md transition-all"
            >
              <TripAdvisorLogo className="w-4 h-4" />
              <span className="[&>svg]:fill-white">{lang === 'es' ? 'Reseñar en Tripadvisor' : 'Review us on Tripadvisor'}</span>
            </a>
          )}
        </div>
      </div>

      {/* Cards */}
      {filtered.length === 0 ? (
        <p className="text-center text-gray-400 py-10">{lang === 'es' ? 'No hay reseñas para mostrar.' : 'No reviews to show.'}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((review) => (
            <ReviewCard key={review.id} review={review} lang={lang} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsWidget;
